import React, { MutableRefObject, useEffect, useState } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import CreditCardForm from '../Recurly/Forms/CreditCardForm';
import PaymentTabs from './PaymentTabs/PaymentTabs';
import { CREDIT_CARD } from '../../utils/constants';
import { ICardPayForm } from '../../../interfaces';
import CountrySelectRecurlyForm from '../CountrySelect/CountrySelect';
import { usePaymentContext } from '../../ContextProviders/PaymentContext';
import { useHCaptchaContext } from '../../ContextProviders/HCaptchaContext';
import PayPal from './Payments/PayPal';
import ApplePay from './Payments/ApplePay';
import Venmo from './Payments/Venmo';
import useTrackField from '../../hooks/useTrackField';
import styles from './PaymentForm.module.css';
import { loadApplePay, showApplePay } from '../../utils/applePay';

const PaymentForm: React.FunctionComponent<ICardPayForm> = ({
  payFieldsState,
  billingCountry,
  flags,
  fieldTracking,
  checkoutTotal,
  setFormError = (errorMessage) => null,
  scrollToForm = () => null,
  showVenmoOption = false,
  datadogLogs,
}) => {
  const { recurlyToken, setRecurlyToken, billingMethod, setBillingMethod, setRecurlyError } =
    usePaymentContext();
  const { hCaptchaToken, setHCaptchaToken, setExecuteHCaptcha } = useHCaptchaContext();
  const formRef = React.useRef() as MutableRefObject<HTMLFormElement>;
  const recurly = useRecurly();
  const [applePayLoaded, setApplePayLoaded] = useState(false);
  const [showApplePayOption, setShowApplePayOption] = useState(false);
  const { fields: fieldsState } = payFieldsState;
  useTrackField(fieldsState, billingMethod, fieldTracking);
  const isCreditCardPayment = billingMethod === CREDIT_CARD;

  function handleSubmit(e?: React.SyntheticEvent) {
    if (e) {
      e.preventDefault();
    }

    recurly.token(formRef.current, async (err: any, token: any): Promise<any> => {
      if (err) {
        datadogLogs.logger.error('CC token error', {}, err);
        setHCaptchaToken('');
        setExecuteHCaptcha(false);
        setRecurlyError(err);
        if (err.code === 'validation') {
          setFormError('Please fill out the missing fields.');
        } else {
          setFormError(err.message);
        }
        scrollToForm();
      }
      if (token) {
        setRecurlyToken(token.id);
        setFormError('');
      }
    });
  }

  useEffect(() => {
    if (hCaptchaToken && isCreditCardPayment && !recurlyToken) {
      handleSubmit();
    }
  }, [hCaptchaToken, isCreditCardPayment, recurlyToken]);

  useEffect(() => {
    const isApplePayAvailable = showApplePay(flags, setShowApplePayOption, showApplePayOption);
    if (!applePayLoaded) {
      loadApplePay(isApplePayAvailable, setApplePayLoaded);
    }
  }, [applePayLoaded]);

  return (
    <>
      <PaymentTabs
        billingMethod={billingMethod}
        setBillingMethod={setBillingMethod}
        showVenmoOption={showVenmoOption}
        showApplePayOption={showApplePayOption}
      />
      <form
        ref={formRef}
        className={
          isCreditCardPayment
            ? styles.pfContainer
            : `${styles.pfContainer} ${styles.pfContainerHidden}`
        }
      >
        <CreditCardForm
          location="GTB"
          name={fieldsState.first_name}
          lastName={fieldsState.last_name}
          number={fieldsState.number}
          month={fieldsState.month}
          year={fieldsState.year}
          cvv={fieldsState.cvv}
          postalCode={fieldsState.postal_code}
          billingCountry={billingCountry}
        />
        <div className={styles.pfRow} hidden>
          <CountrySelectRecurlyForm
            fieldState={fieldsState.country}
            billingCountry={billingCountry}
          />
        </div>
      </form>
      <PayPal recurly={recurly} />
      {showApplePayOption && <ApplePay recurly={recurly} checkoutTotal={checkoutTotal} />}
      {showVenmoOption && <Venmo recurly={recurly as any} checkoutTotal={checkoutTotal} />}
    </>
  );
};

export default PaymentForm;
